import {
  Divider,
  addImageSizeParametersToUrl,
  Badge,
  Transition,
} from '@lounge-fe/ui-kit'

import { ShopifySectionComponent } from '../../types/ShopifySectionComponent'
import { ProductData } from './types'
import React, { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import ProductInformation from './ProductInformation'
import { Recommendations } from './Recommendations'
import Media from './Media'
import Offer from './Offer'
import Title from './Title'
import classNames from 'classnames'
import { Image, Video } from '@/types/LiquidObjects'
import { ProductNotice } from './ProductNotice'
import { GiftCardMedia } from './GiftCardMedia'
import { useGiftCard } from './hooks/useGiftCard'
import { captureException } from '@sentry/react'
import { Features } from './Features'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useProductBadges } from '@/features/product/components/StandardProductCard/hooks/useProductBadges'
import { UpsellsDrawer } from './UpsellDrawer'
import { ProductForm } from './ProductForm'
import { useProductPageStore } from './hooks/useProductPageStore'
import Swatches from './Swatches'
import { ProductDetails } from './ProductDetails/ProductDetails'
import { Price } from './Price'
import { useSearchParams } from '@/hooks/useSearchParams'
import { Outfit } from '../../features/product/components/Outfit'
import { CompositionCare } from './CompositionCare'
import { Reviews } from './Reviews'
import { type BraLiftType } from './BraLift'
import { PriceRange } from '@/features/product/components/StandardProductCard/PriceRange'
import { ProductPagePriceRange } from './ProductPagePriceRange'

const BraLift = React.lazy(async () => ({
  default: (await import('./BraLift/BraLift')).BraLift,
}))

export const ProductPageInner: ShopifySectionComponent<ProductData> = ({
  data: {
    accordions = [],
    back_in_stock,
    bra_lift,
    collection_handles = [],
    composition_care,
    delivery_returns_notices = [],
    features = [],
    fit_fabric,
    offers = [],
    okendo_review_average,
    okendo_review_count,
    outfit,
    payment_providers,
    product,
    product_notice,
    related_products,
    size_guide,
    upsells_settings,
  } = {},
}) => {
  const {
    id,
    media = [],
    title,
    swatches = [],
    upsells = [],
    options = [],
    tags = [],
    badges = [],
    price = 0,
    variant_min_price,
    variant_max_price,
    compare_at_price,
    color,
    canonical_color,
    gift_card = false,
    has_price_override = false,
    hide_out_of_stock_form: hideOutOfStockForm,
  } = product || {}

  const { replace } = useSearchParams()

  const ProductInformationRef = useRef<HTMLDivElement>(null)

  const {
    size_guides = [],
    primary_action,
    secondary_action,
  } = size_guide || {}

  const { t } = useTranslation()
  const { getFlag } = useFeatureFlags()

  const setSelectedVariant = useProductPageStore(
    (state) => state.setSelectedVariant,
  )
  const selectedVariant = useProductPageStore((state) => state.selectedVariant)
  const isDetailsOpen = useProductPageStore((state) => state.isDetailsOpen)

  const activePrice = (!has_price_override && selectedVariant?.price) || price
  const compareAtPrice =
    (!has_price_override && selectedVariant?.compare_at_price) ||
    compare_at_price

  const { primaryBadge, secondaryBadge } = useProductBadges({
    badges,
    price: activePrice,
    price_compare_at: compareAtPrice ?? 0,
    tags,
  })

  const giftcard = useGiftCard({
    isGiftCard: gift_card,
    options,
    enabled: getFlag('product_gift_card_recipient'),
  })

  const relevantOffers = useMemo(
    () =>
      offers.filter(
        (offer) =>
          offer.collection && collection_handles.includes(offer.collection),
      ),
    [offers],
  )

  const inStockUpsells = useMemo(
    () =>
      upsells
        .filter(
          (upsell) =>
            upsell.variants.find((v) => v.in_stock) && !!upsell.images?.length,
        )
        .slice(0, 4),
    [upsells],
  )

  useEffect(() => {
    if (isDetailsOpen) {
      ProductInformationRef.current
        ?.querySelector('[role="dialog"]')
        ?.scrollIntoView({
          block: 'nearest',
        })
    }
  }, [isDetailsOpen])

  useEffect(() => {
    if (selectedVariant) {
      replace({ variant: selectedVariant.id.toString() })
    } else {
      replace({
        variant: undefined,
      })
    }
  }, [selectedVariant])

  const reviewCount = Number(okendo_review_count)
  const reviewAverage = okendo_review_average ? okendo_review_average : 0

  const hasVariablePrice =
    variant_min_price &&
    variant_max_price &&
    variant_min_price !== variant_max_price

  return (
    <div className="sm:mb-10">
      <div className="relative flex flex-col flex-1 md:mb-16 md:flex-row gap-x-0">
        {giftcard.enabled && selectedVariant?.image ? (
          <GiftCardMedia image={selectedVariant.image} />
        ) : (
          <Media
            className="self-start sticky flex-1 top-[var(--header-height)]"
            productId={product?.id}
            options={{
              hasBraLift: getFlag('gravity_bra_lift') && !!bra_lift?.lift,
            }}
          >
            {media.map((file, index: number) => {
              const altText = `${product?.title} | ${file.alt}`
              return file.media_type === 'video' ? (
                <Media.Video
                  key={index}
                  className={classNames(
                    'w-full object-cover object-center h-full',
                    index % 3 === 0 && 'col-span-2',
                  )}
                  alt={altText}
                >
                  {(file as Video).sources
                    .sort((a: any, b: any) => b.width - a.width)
                    .map((source: any, index: number) => (
                      <source
                        key={index}
                        src={source.url}
                        type={source.mime_type}
                        width={source.width}
                        height={source.height}
                      />
                    ))}
                </Media.Video>
              ) : (
                <Media.Image
                  key={index}
                  className={classNames(
                    'w-full object-cover object-center h-full',
                  )}
                  alt={altText}
                  data={{
                    src: (file as Image).src,
                    width: 679,
                    height: 679,
                    crop: 'center',
                  }}
                  index={index}
                  zoomSrc={addImageSizeParametersToUrl({
                    src: (file as Image).src,
                    width: 1920,
                    height: 1920,
                    crop: 'center',
                    onError: captureException,
                  })}
                  loading={index === 0 ? 'eager' : 'lazy'}
                />
              )
            })}
          </Media>
        )}

        <ProductInformation
          data-testid="product-info"
          className="sm:pt-6 flex-1"
          ref={ProductInformationRef}
        >
          <Transition.Fade show={!isDetailsOpen}>
            {!isDetailsOpen && (
              <>
                <div className="flex flex-row gap-2">
                  {secondaryBadge && (
                    <Badge
                      className="mb-1 md:mb-2"
                      variant="outline"
                      color={secondaryBadge.color}
                      size="sm"
                    >
                      {secondaryBadge.text}
                    </Badge>
                  )}
                  {primaryBadge && (
                    <Badge
                      className="mb-1 md:mb-2"
                      variant="ghost"
                      color={primaryBadge.color}
                      size="sm"
                    >
                      {primaryBadge.text}
                    </Badge>
                  )}
                </div>
                <div className="flex justify-between items-start mb-2 gap-4">
                  <Title>
                    {title} <span className="sr-only">- {color}</span>
                  </Title>

                  {hasVariablePrice && !selectedVariant ? (
                    <ProductPagePriceRange
                      minPrice={variant_min_price / 100}
                      maxPrice={variant_max_price / 100}
                    />
                  ) : (
                    <Price
                      price={activePrice / 100}
                      compareAtPrice={compareAtPrice && compareAtPrice / 100}
                    />
                  )}
                </div>
                {reviewCount > 0 && (
                  <Reviews
                    productId={product?.id}
                    variant="productInfo"
                    reviewCount={reviewCount}
                    avgRating={reviewAverage}
                    productImage={product?.images[0]}
                  />
                )}
                {(swatches.length > 0 || (color && id)) && (
                  <Swatches
                    selectedId={id}
                    swatches={[
                      {
                        id: id!,
                        color: color ?? '',
                        canonical_color,
                        url: '#',
                      },
                      ...swatches.map((swatch) => ({
                        id: swatch.id,
                        color: swatch.color,
                        canonical_color: swatch.canonical_color,
                        url: `/products/${swatch.handle}`,
                        title: swatch.title,
                      })),
                    ]}
                  />
                )}
                {product_notice && product_notice?.notice && (
                  <ProductNotice
                    className="mt-4 [&_a]:underline"
                    notice={product_notice.notice}
                  />
                )}

                {product?.id && (
                  <ProductForm
                    product={product}
                    hasOnlyDefaultVariant={product?.has_only_default_variant}
                    options={product?.options ?? []}
                    variants={product?.variants ?? []}
                    linked_bottoms={product?.linked_bottoms ?? []}
                    hideOutOfStockForm={hideOutOfStockForm}
                    sizeGuides={size_guides}
                    primaryAction={primary_action}
                    secondaryAction={secondary_action}
                    backInStock={back_in_stock}
                    giftcard={gift_card}
                    onVariantSelection={setSelectedVariant}
                    showWishlistButton={!gift_card}
                    className="gap-8 my-6"
                  />
                )}
                {features.length > 2 && (
                  <Features className="mb-8" features={features} />
                )}
                {inStockUpsells.length > 0 && (
                  <UpsellsDrawer
                    title={upsells_settings?.title}
                    content={upsells_settings?.content}
                    upsells={inStockUpsells.map((upsell) => ({
                      ...upsell,
                      image: {
                        data: {
                          src: upsell.images[0].src,
                          width: 200,
                          height: 200,
                          crop: 'center',
                        },
                        alt: upsell.images[0].alt,
                      },
                      secondaryImage: upsell.images[1]
                        ? {
                            data: {
                              src: upsell.images[1].src,
                              width: 200,
                              height: 200,
                              crop: 'center',
                            },
                            alt: upsell.images[1].alt,
                          }
                        : undefined,
                      placeholder: t('product.select_size'),
                      compareAtPrice: upsell.compare_at_price,
                      showPrice: upsells_settings?.show_price ?? false,
                      linkedBottoms: upsell.linked_bottoms ?? [],
                      hasOnlyDefaultVariant: upsell?.has_only_default_variant,
                    }))}
                  />
                )}
                {relevantOffers.map((offer, index) => (
                  <Offer className="mb-4" key={index}>
                    <Offer.Title>{offer.title}</Offer.Title>
                    <Offer.Subtitle>{offer.subtitle}</Offer.Subtitle>
                    <Offer.Body>{offer.body}</Offer.Body>
                    <Offer.Link
                      href={
                        offer.button_url ?? `/collections/${offer.collection}`
                      }
                    >
                      {offer.button_text}
                    </Offer.Link>
                  </Offer>
                ))}
                <Divider className="my-4" />
              </>
            )}
          </Transition.Fade>

          <ProductDetails
            fitAndFabric={fit_fabric}
            deliveryReturnsNotices={delivery_returns_notices}
            accordions={accordions}
            product={product}
          />
        </ProductInformation>
      </div>

      {outfit && <Outfit outfit={outfit} />}

      {composition_care && (
        <CompositionCare compositionCare={composition_care} />
      )}

      {getFlag('gravity_bra_lift') && bra_lift && (
        <BraLift
          lift={bra_lift.lift as BraLiftType}
          title={bra_lift.title}
          description={bra_lift.description}
          media={bra_lift.media}
        />
      )}

      {related_products && (
        <Recommendations
          title={related_products.carousel_title}
          minRecommendations={related_products.min_results}
          maxRecommendations={related_products.max_results}
          productId={id}
          filterProducts={[...swatches.map(({ id }) => id)]}
        />
      )}
    </div>
  )
}

const ProductPage: ShopifySectionComponent<ProductData> = ({ ...props }) => {
  return <ProductPageInner {...props} />
}

export default ProductPage
